import React, { useState, useEffect, useRef } from 'react';
import { DEFAULT_PUBLIC_SITE_URL, fitShareImageUrl, normalizePublicSiteUrl, rememberPublicSiteUrl } from '../../utils/publicSiteUrl';
import './LogoManagement.css'; // Reuse the same styles

interface ShareImageData {
  imageUrl: string;
  altText: string;
  publicId?: string;
  publicSiteUrl?: string;
  width?: number;
  height?: number;
  updatedAt?: string;
}

const ShareImageManagement: React.FC = () => {
  const [shareImage, setShareImage] = useState<ShareImageData>({
    imageUrl: '',
    altText: 'Carmen Lorraine Photography - Package Finder'
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [publicSiteUrl, setPublicSiteUrl] = useState(DEFAULT_PUBLIC_SITE_URL);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [showUploadWidget, setShowUploadWidget] = useState(false);
  const [cloudinaryLoaded, setCloudinaryLoaded] = useState(false);
  const uploadWidgetRef = useRef<any>(null);

  useEffect(() => {
    loadShareImage();
    loadCloudinaryScript();
  }, []);

  const loadCloudinaryScript = () => {
    if ((window as any).cloudinary) {
      setCloudinaryLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    script.onload = () => {
      setCloudinaryLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load Cloudinary script');
      setUploadError('Failed to load Cloudinary. Please refresh the page.');
    };
    document.head.appendChild(script);
  };

  const loadShareImage = async () => {
    try {
      const response = await fetch('/api/share-image');
      if (response.ok) {
        const serverData = await response.json();
        if (serverData && (serverData.imageUrl || serverData.publicSiteUrl)) {
          const siteUrl = normalizePublicSiteUrl(serverData.publicSiteUrl) || DEFAULT_PUBLIC_SITE_URL;
          setPublicSiteUrl(siteUrl);
          rememberPublicSiteUrl(siteUrl);
          setShareImage(prev => ({ ...prev, ...serverData, publicSiteUrl: siteUrl }));
          if (serverData.imageUrl) {
            localStorage.setItem('package_share_image', JSON.stringify({ ...serverData, publicSiteUrl: siteUrl }));
          }
          return;
        }
      }
    } catch (error) {
      console.warn('Share image not available from server, falling back to localStorage.');
    }

    const imageData = localStorage.getItem('package_share_image');
    if (imageData) {
      try {
        const parsed = JSON.parse(imageData);
        setShareImage(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Error loading share image:', error);
      }
    }
  };

  const saveShareImage = async (imageData: ShareImageData) => {
    try {
      const siteUrl = normalizePublicSiteUrl(imageData.publicSiteUrl || publicSiteUrl) || DEFAULT_PUBLIC_SITE_URL;
      const updatedImageData = { ...imageData, publicSiteUrl: siteUrl, updatedAt: new Date().toISOString() };
      const imageJson = JSON.stringify(updatedImageData);
      if (updatedImageData.imageUrl) {
        localStorage.setItem('package_share_image', imageJson);
      }
      rememberPublicSiteUrl(siteUrl);
      setShareImage(updatedImageData);
      
      window.dispatchEvent(new CustomEvent('share_image_updated', { detail: updatedImageData }));

      const response = await fetch('/api/share-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedImageData)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to save share settings');
      }
      
      setUploadSuccess('Share image updated successfully!');
      setTimeout(() => setUploadSuccess(null), 3000);
      setUploadError(null);
    } catch (error) {
      console.error('Error saving share image:', error);
      setUploadError(`Failed to save share image: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setUploadSuccess(null);
    }
  };

  const handleUploadSuccess = (result: any) => {
    try {
      let imageUrl = '';
      let publicId = '';
      let width = 0;
      let height = 0;
      
      if (result.info) {
        if (typeof result.info === 'object') {
          imageUrl = result.info.secure_url || result.info.url || '';
          publicId = result.info.public_id || '';
          width = result.info.width || 0;
          height = result.info.height || 0;
        }
      } else if (result.secure_url || result.url) {
        imageUrl = result.secure_url || result.url || '';
        publicId = result.public_id || '';
        width = result.width || 0;
        height = result.height || 0;
      }
      
      if (!imageUrl) {
        setUploadError('Upload succeeded but no image URL was returned. Please try again.');
        setIsUploading(false);
        setShowUploadWidget(false);
        return;
      }
      
      const imageData: ShareImageData = {
        imageUrl: imageUrl,
        altText: shareImage.altText,
        publicId: publicId,
        publicSiteUrl,
        width: width,
        height: height
      };
      
      saveShareImage(imageData);
      setIsUploading(false);
      setShowUploadWidget(false);
      setUploadError(null);
    } catch (error) {
      console.error('Error processing upload result:', error);
      setUploadError('Error processing uploaded image. Please try again.');
      setIsUploading(false);
      setShowUploadWidget(false);
    }
  };

  const savePublicSiteUrl = () => {
    const siteUrl = normalizePublicSiteUrl(publicSiteUrl);
    if (!siteUrl) {
      setUrlError('Use a public https address such as https://carmenlorraine.com. Localhost is not allowed.');
      return;
    }
    setUrlError(null);
    setPublicSiteUrl(siteUrl);
    saveShareImage({ ...shareImage, publicSiteUrl: siteUrl });
  };

  const openUploadWidget = () => {
    if (!cloudinaryLoaded) {
      setUploadError('Cloudinary is still loading. Please wait a moment and try again.');
      return;
    }
    
    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    
    if (uploadWidgetRef.current) {
      uploadWidgetRef.current.open();
    } else {
      // Get Cloudinary cloud name
      const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'di1mzmlts';
      
      // Try different upload presets in order of preference (same as other components)
      const uploadPresets = ['unsigned_upload', 'carmen_lorraine_photos', 'ml_default'];
      const selectedPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || uploadPresets[0];
      
      // Create upload widget configuration
      const widgetConfig: any = {
        cloudName: cloudName,
        uploadPreset: selectedPreset,
        folder: 'carmen_lorraine_photos/share_images',
        maxFiles: 1,
        resourceType: 'image',
        cropping: false,
        multiple: false,
        sources: ['local', 'url'],
        maxImageWidth: 2400,
        maxImageHeight: 2400,
        clientAllowedFormats: ['jpg', 'jpeg', 'png', 'webp'],
        theme: 'minimal'
      };
      
      // Create upload widget using window.cloudinary
      const cloudinaryWidget = (window as any).cloudinary?.createUploadWidget(
        widgetConfig,
        (error: any, result: any) => {
          if (error) {
            console.error('Cloudinary upload error:', error);
            setUploadError('Upload failed. Please try again.');
            setIsUploading(false);
            return;
          }
          
          if (!result) {
            return;
          }
          
          // Handle different event types
          if (result.event === 'success') {
            handleUploadSuccess(result);
          } else if (result.event === 'close') {
            setIsUploading(false);
            setShowUploadWidget(false);
          } else if (result.event === 'abort') {
            setIsUploading(false);
            setShowUploadWidget(false);
          }
        }
      );
      
      if (cloudinaryWidget) {
        uploadWidgetRef.current = cloudinaryWidget;
        cloudinaryWidget.open();
      } else {
        setUploadError('Cloudinary upload widget not available. Please refresh the page and try again.');
        setIsUploading(false);
      }
    }
  };

  return (
    <section className="admin-section logo-management">
      <div className="section-header">
        <h2>Package share image</h2>
        <p>The picture used when a package is shared, and the public website address.</p>
      </div>

      <div className="section-content">
        <div className="alt-text-editor" style={{ maxWidth: 720, marginBottom: '2rem' }}>
          <label htmlFor="public-site-url">Public website address</label>
          <input
            type="url"
            id="public-site-url"
            value={publicSiteUrl}
            onChange={(e) => {
              setPublicSiteUrl(e.target.value);
              setUrlError(null);
            }}
            placeholder="https://carmenlorraine.com"
            className="form-control"
          />
          <small className="form-help">
            Share links use this address. A local admin session cannot put localhost into a share link.
          </small>
          {urlError && (
            <p className="form-help" style={{ color: '#9b2c2c' }}>{urlError}</p>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={savePublicSiteUrl}
            style={{ marginTop: '0.75rem' }}
          >
            Save address
          </button>
        </div>

        {uploadError && (
          <div className="alert alert-error">
            <i className="fas fa-exclamation-triangle"></i>
            {uploadError}
          </div>
        )}

        {uploadSuccess && (
          <div className="alert alert-success">
            <i className="fas fa-check-circle"></i>
            {uploadSuccess}
          </div>
        )}

        <div className="logo-management-grid">
          {/* Current Share Image Display */}
          <div className="logo-preview-section">
            <h3>Current Share Image</h3>
            <div className="logo-preview-container share-image-preview-container">
              {shareImage.imageUrl ? (
                <div className="logo-display">
                  <div className="share-image-frame">
                    <img
                      src={fitShareImageUrl(shareImage.imageUrl) || shareImage.imageUrl}
                      alt={shareImage.altText}
                      className="share-image-preview"
                    />
                  </div>
                  <div className="logo-info">
                    <p><strong>Alt Text:</strong> {shareImage.altText}</p>
                    {shareImage.width && shareImage.height && (
                      <p><strong>Dimensions:</strong> {shareImage.width} × {shareImage.height}px</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-logo-placeholder">
                  <i className="fas fa-image"></i>
                  <p>No share image uploaded</p>
                  <p className="placeholder-subtitle">Upload a share image to get started</p>
                </div>
              )}
            </div>
          </div>

          {/* Share Image Management Actions */}
          <div className="logo-actions-section">
            <h3>Share Image Actions</h3>
            
            <div className="action-buttons">
              <button
                className="btn btn-primary"
                onClick={openUploadWidget}
                disabled={isUploading || !cloudinaryLoaded}
              >
                {!cloudinaryLoaded ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Loading...
                  </>
                ) : isUploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-upload"></i>
                    {shareImage.imageUrl ? 'Replace Share Image' : 'Upload Share Image'}
                  </>
                )}
              </button>
            </div>

            {/* Alt Text Editor */}
            <div className="alt-text-editor">
              <label htmlFor="share-image-alt-text">Alt Text (for accessibility)</label>
              <input
                type="text"
                id="share-image-alt-text"
                value={shareImage.altText}
                onChange={(e) => setShareImage(prev => ({ ...prev, altText: e.target.value }))}
                onBlur={() => saveShareImage(shareImage)}
                placeholder="Enter alt text for the share image"
                className="form-control"
              />
              <small className="form-help">
                This text will be used by screen readers and when the image cannot be displayed.
              </small>
            </div>
          </div>
        </div>

        {/* Share Image Guidelines */}
        <div className="logo-guidelines">
          <h3>Share Image Guidelines</h3>
          <div className="guidelines-content">
            <div className="guideline-item">
              <i className="fas fa-info-circle"></i>
              <div>
                <h4>Recommended Dimensions</h4>
                <p>The image is fitted inside a 1200×630 share frame. The whole image stays visible, with space added when its shape does not already match.</p>
              </div>
            </div>
            <div className="guideline-item">
              <i className="fas fa-file-image"></i>
              <div>
                <h4>Supported Formats</h4>
                <p>PNG, JPG, JPEG, and WebP formats are supported</p>
              </div>
            </div>
            <div className="guideline-item">
              <i className="fas fa-share-alt"></i>
              <div>
                <h4>Usage</h4>
                <p>This image appears when packages are shared on social media platforms (WhatsApp, Facebook, Twitter, etc.)</p>
              </div>
            </div>
            <div className="guideline-item">
              <i className="fas fa-image"></i>
              <div>
                <h4>Fallback</h4>
                <p>Gallery shares use that gallery’s hero image. This image is used when a page has no hero. If this image is empty, the website logo is used. Local files are ignored.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShareImageManagement;

